/** @format */

import { Component, Input, OnInit } from "@angular/core";
import { ToastService } from "mis-crystal-design-system/toast";
import { Moment, parseZone } from "moment-timezone";
import { Observable, throwError, combineLatest, ReplaySubject } from "rxjs";
import { catchError, map, take, switchMap } from "rxjs/operators";
import { AsyncWidget } from "mis-crystal-design-system/widgets";
import { DrawerService } from "mis-design-system/drawer";
import { NsWISConfigurations } from "../../models/configurations.model";
import { NsEmpWebExpData } from "../../models/emp-web-exp.model";
import { ApiService } from "../../services/api.service";
import { BookingDataService } from "../../services/booking-data.service";
import { ConfigurationService } from "../../services/configuration.service";
import { EmpUserService } from "../../services/emp-user.service";
import { UpcomingBookingsComponent } from "../modals/upcoming-bookings/upcoming-bookings.component";
import { minLength } from "../../functions/booking-utils.functions";
import { CONSTANTS_ASSETS_EMP_WEB_EXP } from "../../constants/assets.constants";
import { IVirtualScrollApi, IVirtualScrollConfig } from "mis-crystal-design-system/virtual-scroll";

@Component({
  selector: "mis-employee-web-exp-history",
  templateUrl: "./history.component.html",
  styleUrls: ["./history.component.scss"]
})
export class HistoryComponent implements OnInit {
  @Input() buid: string = "b33bc098-c909-4247-bec5-da5173e4f476";
  @Input() empGuid: string = "4e6b81bb-e62b-4bde-b55b-dc5ed00c69f4";
  configurations: NsWISConfigurations.IConfigurations;
  widget: AsyncWidget;
  dateRangeValue: [Date, Date] = [new Date(), new Date()];
  dateRangePickerConfig = {};
  dateRangeStream = new ReplaySubject<[Moment, Moment]>(1);
  ASSETS = CONSTANTS_ASSETS_EMP_WEB_EXP;
  bookingsLength: number = 0;
  totalBookings: number = 0;

  // virtual scroll related variables
  api: IVirtualScrollApi;
  config: IVirtualScrollConfig;
  error: boolean = false;
  isLoading: boolean = true;
  upcomingBookingOffset: number = 0;
  upcomingBookingPageSize: number = 10;

  constructor(
    private user: EmpUserService,
    private configSvc: ConfigurationService,
    private bookingSvc: BookingDataService,
    private apiService: ApiService,
    private toast: ToastService,
    private drawerSvc: DrawerService
  ) {
    this.config = {
      rowHeight: 44,
      pageSize: 10,
      infiniteScrollDataFunction: this.genUpcomingBookingStream.bind(this)
    };
  }

  ngOnInit(): void {
    if (!this.empGuid || !this.buid) {
      throw new Error("missing required inputs buid & empGUid");
    }
    this.user.setUser(this.buid, this.empGuid);
    this.configSvc.refreshReplayStreams();
    this.bookingSvc.refreshReplayStreams();

    combineLatest([
      this.apiService.getEmpTimezone().pipe(
        catchError(err => {
          this.isLoading = false;
          this.toast.displayMsg(this.toast.messageBuilder.error.simple);
          return throwError(`Failed to load timezone. ${JSON.stringify(err)}`);
        })
      ),
      this.configSvc.configurations.pipe(
        catchError(err => {
          this.isLoading = false;
          this.toast.displayMsg(this.toast.messageBuilder.error.simple);
          return throwError(`Failed to load Configurations. ${JSON.stringify(err)}`);
        })
      )
    ]).subscribe(([timezone, configs]) => {
      this.configurations = configs;
      this.bookingSvc.timezone = timezone;
      const start = parseZone().tz(timezone).subtract(7, "days");
      const end = parseZone().tz(timezone).subtract(1, "days");
      this.dateRangePickerConfig = {
        containerClass: "booking-history-date-range-picker theme-green",
        minDate: parseZone().tz(timezone).subtract(90, "days").startOf("day").toDate(),
        maxDate: end.toDate(),
        rangeInputFormat: configs.flattenedSettings.DATE_FORMAT?.replace(/y/g, "Y")
      };
      this.dateRangeValue = [start.toDate(), end.toDate()];
      this.dateRangeStream.next([start, end]);
      this.isLoading = true;
      setTimeout(() => {this.api.switchOnInfiniteScroll()}, 100)
    });
  }

  genUpcomingBookingStream(offset: number, pageSize: number): Observable<NsEmpWebExpData.IList[]> {
    this.isLoading = true;
    this.upcomingBookingOffset = offset;
    this.upcomingBookingPageSize = pageSize;
    return this.dateRangeStream.pipe(
      take(1),
      switchMap(([start, end]) => {
        return this.apiService
          .getEmpProfile(
            this.apiService.genEmpDetailQuery(
              start.startOf("day").valueOf(),
              end.endOf("day").valueOf(),
              ["OFFICE", "WFH", "PARKING_TWO", "PARKING_FOUR", "MEALS"],
              false,
              true,
              offset,
              pageSize
            )
          )
          .pipe(
            map(res => {
              this.bookingsLength = 0;
              if (res.dataPresent) {
                const bookings = this.bookingSvc.organiseBookings(
                  res?.data?.getEmployee?.[0]?.bookingDetailDTOS || [],
                  this.configurations.flattenedSettings.approvalFlowEnabled
                );
                this.bookingsLength = bookings.length;
                this.totalBookings += this.bookingsLength;
                this.isLoading = false;
                return bookings;
              } else {
                this.isLoading = false;
                this.toast.displayMsg(res.errors?.[0]?.message || "Failed to load bookings!", 5000);
                return [];
              }
            })
          );
      })
    );
  }

  public handleUpcoming(booking: NsEmpWebExpData.IBookingDetailDTOS): void {
    const ref = this.drawerSvc.show(UpcomingBookingsComponent, { booking, configs: this.configurations });
    ref.afterClosed.subscribe((res) => {
      if(!!res && res === 'MEAL_FEEDBACK'){
        this.isLoading = true;
        this.api.switchOnInfiniteScroll();
      }
    })
  }

  public changeDateRange(dateRangeValue) {
    this.totalBookings = 0;
    const [start, end] = dateRangeValue;
    if (parseZone(end).diff(parseZone(start), "days") >= 30) {
      this.toast.displayMsg("You can select maximum of 30 days at a time.", 3000);
      return;
    }
    if (dateRangeValue === this.dateRangeValue) return;

    this.dateRangeValue = dateRangeValue;
    this.dateRangeStream.next([parseZone(start), parseZone(end)]);
    this.isLoading = true;
    this.api.switchOnInfiniteScroll();
  }
}
